import { createContext, useEffect, useState, useCallback, useRef } from "react";
import { getAllData } from "../services/api";
import { addCustomer, updateCustomer, deleteCustomer } from "../services/customer";
import { addSchemeCard, updateSchemeCard, deleteSchemeCard } from "../services/schemecard";
import { addCustomerTransaction, updateCustomerTransaction, deleteCustomerTransaction, addCustomerTransactionAndUpdateStock as apiAddCustomerTransactionAndUpdateStock, removeCustomerTransactionAndUpdateStockApi } from "../services/customertransaction";
import { addStock, updateStock, deleteStock, addBulkStock } from "../services/stock";
import { addSupplier, updateSupplier, deleteSupplier } from "../services/supplier";
import { addGirvi, updateGirvi, deleteGirvi } from "../services/girvi";
import { addGirviPayment, updateGirviPayment, deleteGirviPayment } from "../services/girviPayment";
import { addSupplierTransaction, updateSupplierTransaction, deleteSupplierTransaction } from "../services/supplierTransaction";
import { closeRoznamaDay, addRoznamaDay, updateRoznamaDay, deleteRoznamaDay } from "../services/roznama";
import {
  updateCompanyDetails,
  addCompanyDetails as apiAddCompanyDetails,
  login as apiLogin,
  sendPasswordResetOtp,
  verifyPasswordResetOtp,
  resetPassword,
  updatePassword as apiUpdatePassword,
} from "../services/auth";
import { sessionManager } from "./sessionManager";
import {
  addAdvancePayment as apiAddAdvancePayment,
  getCustomerAdvancePayments,
  advanceUseForBill,
  getAllAdvancePayments,
  getAdvancePaymentSummary
} from "../services/advancepayment";
import {
  getGrossStock as apiGetGrossStock,
  getStockSummary as apiGetStockSummary,
  getCurrentStock as apiGetCurrentStock,
  addGrossStockItem as apiAddGrossStockItem,
  addStockTransaction as apiAddStockTransaction,
  getStockTransactions as apiGetStockTransactions,
  updateStockTransaction as apiUpdateStockTransaction,
  deleteStockTransaction as apiDeleteStockTransaction,
  bulkAddStockTransactions as apiBulkAddStockTransactions,
  getStockByMetal as apiGetStockByMetal,
  searchItems as apiSearchItems,
  getItemTransactionHistory as apiGetItemTransactionHistory,
  getStockValueSummary as apiGetStockValueSummary
} from "../services/grossStock";
import { useReactDevTools } from "./ReactDevTools";
import {
  addUrdPurchase,
  updateUrdPurchase,
  deleteUrdPurchase
} from "../services/urdpurchase";
import {
  addEInvoice as apiAddEInvoice,
  updateEInvoice as apiUpdateEInvoice,
  deleteEInvoice as apiDeleteEInvoice,
} from "../services/einvoice";

export const DataContext = createContext();

// ================================================================
// LOCAL CACHE CONFIG
// ================================================================
const LS_KEY = "appDataCache_v3";
const LS_TIMESTAMP_KEY = "appDataCache_v3_ts";
const CACHE_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes

const readLocalCache = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const ts = parseInt(localStorage.getItem(LS_TIMESTAMP_KEY) || "0", 10);
    if (!raw) return null;
    if (Date.now() - ts > CACHE_MAX_AGE_MS) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

const writeLocalCache = (data) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(data));
    localStorage.setItem(LS_TIMESTAMP_KEY, String(Date.now()));
  } catch (e) {
    console.warn("localStorage write failed:", e);
  }
};

const clearLocalCache = () => {
  try {
    localStorage.removeItem(LS_KEY);
    localStorage.removeItem(LS_TIMESTAMP_KEY);
  } catch (e) { }
};

const getTodayISTDate = () => {
  const now = new Date();
  const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
  const year = istTime.getUTCFullYear();
  const month = String(istTime.getUTCMonth() + 1).padStart(2, '0');
  const day = String(istTime.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const DataProvider = ({ children }) => {
  const { isSecretMode } = useReactDevTools();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({
    Customers: [],
    AdvancePayments: [],
    CustomerTransactions: [],
    Stock: [],
    Suppliers: [],
    SupplierTransactions: [],
    Girvi: [],
    GirviPayments: [],
    Roznama: [],
    CompanyDetails: [],
    SchemeCard: [],
    GrossStock: [],
    GrossStockTransactions: [],
    URDPurchase: [],
    EInvoices: [],
  });
  const [currentUser, setCurrentUser] = useState(() => sessionManager.getSession());
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!sessionManager.getSession());
  const initialLoadRef = useRef(false);
  const lastFetchRef = useRef(0);

  // ================================================================
  // HELPERS
  // ================================================================
  const formatSoldDescription = useCallback((soldDesc) => {
    if (!soldDesc) return "Not sold";
    try {
      const desc = typeof soldDesc === 'string' ? JSON.parse(soldDesc) : soldDesc;
      return `Sold on: ${desc.sold_date || 'N/A'}, Bill No: ${desc.sold_bill_no || 'N/A'}`;
    } catch (e) {
      return "Sold (details unavailable)";
    }
  }, []);

  const parseSoldDescriptionFromBackend = useCallback((soldDesc) => {
    if (!soldDesc) return null;
    if (typeof soldDesc === 'string') {
      try {
        return JSON.parse(soldDesc);
      } catch {
        return { description: soldDesc };
      }
    }
    return soldDesc;
  }, []);

  const sortByName = (dataArray) => {
    if (!Array.isArray(dataArray)) return dataArray;
    return [...dataArray].sort((a, b) =>
      a.name?.localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
  };

  const generatePaymentId = () => {
    return `pay_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const calculateTotal = (transactions, type = 'all') => {
    if (!Array.isArray(transactions)) return 0;
    return transactions.reduce((sum, t) => {
      const amount = parseFloat(t.amount) || 0;
      const cash = parseFloat(t.cash) || 0;
      const bank = parseFloat(t.bank) || 0;
      if (type === 'cash') return sum + cash;
      if (type === 'bank') return sum + bank;
      return sum + amount;
    }, 0);
  };

  const getDataTypeLabel = (dataType) => {
    const labels = {
      Customers: 'Customer',
      CustomerTransactions: 'Transaction',
      Stock: 'Stock Item',
      Suppliers: 'Supplier',
      SupplierTransactions: 'Supplier Transaction',
      Girvi: 'Girvi',
      GirviPayments: 'Girvi Payment',
      EInvoices: 'E-Invoice',
    };
    return labels[dataType] || dataType;
  };

  // ================================================================
  // MAP RAW API → CONTEXT SHAPE
  // ================================================================
  const mapFetchedData = useCallback((fetchedData) => {
    const parsedStock = (fetchedData?.Stock || []).map(item => ({
      ...item,
      SoldDescription: parseSoldDescriptionFromBackend(item.SoldDescription ?? item.soldDescription)
    }));

    const initializedCustomers = (fetchedData?.Customers || []).map(customer => ({
      ...customer,
      advancePayments: customer.advancePayments || [],
      totalAdvanceBalance: customer.totalAdvanceBalance || 0,
      advanceSummary: customer.advanceSummary || {
        totalPaid: 0,
        totalUsed: 0,
        totalRemaining: 0
      }
    }));

    const parsedEInvoices = (fetchedData?.EInvoice || []).map((inv) => {
      let items = inv.itemDetails;
      if (typeof items === "string") {
        try { items = JSON.parse(items); } catch { items = []; }
      }
      return {
        ...inv,
        customerId: inv.customerId != null ? String(inv.customerId) : "",
        itemDetails: Array.isArray(items) ? items : [],
        taxableAmount: Number(inv.taxableAmount) || 0,
        cgst: Number(inv.cgst) || 0,
        sgst: Number(inv.sgst) || 0,
        igst: Number(inv.igst) || 0,
        roundOff: Number(inv.roundOff) || 0,
        grandTotal: Number(inv.grandTotal) || 0,
      };
    });

    return {
      Customers: initializedCustomers,
      CustomerTransactions: fetchedData?.CustomerTransactions || [],
      Stock: parsedStock,
      Suppliers: fetchedData?.Suppliers || [],
      SupplierTransactions: fetchedData?.SupplierTransactions || [],
      Girvi: fetchedData?.Girvi || [],
      GirviPayments: fetchedData?.GirviPayments || [],
      Roznama: fetchedData?.Roznama || [],
      CompanyDetails: fetchedData?.CompanyDetails || [],
      SchemeCard: fetchedData?.SchemeCard || [],
      GrossStock: fetchedData?.GrossStock || [],
      GrossStockTransactions: fetchedData?.GrossStockTransaction || fetchedData?.GrossStockTransactions || [],
      AdvancePayments: fetchedData?.AdvancePayments || [],
      URDPurchase: fetchedData?.URDPurchase || [],
      EInvoices: parsedEInvoices,
    };
  }, [parseSoldDescriptionFromBackend]);

  // ================================================================
  // LOAD INITIAL DATA — Stale-While-Revalidate
  // ================================================================
  const loadInitialData = useCallback(async (opts = {}) => {
    const { forceRefresh = false } = opts;
    const now = Date.now();

    if (!forceRefresh && now - lastFetchRef.current < 2000) {
      console.log("⏭️ Skipping fetch — too soon");
      return;
    }
    lastFetchRef.current = now;

    // 1. Show cached data instantly
    if (!forceRefresh) {
      const cached = readLocalCache();
      if (cached) {
        const mapped = mapFetchedData(cached);
        setData(prev => ({ ...prev, ...mapped }));
        setLoading(false);
        console.log("✅ Rendered from localStorage cache");
      } else {
        setLoading(true);
      }
    } else {
      setLoading(true);
    }

    // 2. Fetch fresh
    setRefreshing(true);
    try {
      const action = isSecretMode ? "getAllData" : "getData";
      const response = await getAllData(action);
      console.log("API response:", response);
      const fetchedData = response?.data || response || {};

      const meta = fetchedData._meta || {};
      if (meta.errors?.length) {
        console.warn("⚠️ Some sheets failed on server:", meta.errors);
      }
      if (meta.elapsedMs) {
        console.log(`🌐 Fetched in ${meta.elapsedMs}ms (cached: ${meta.cached ?? false})`);
      }

      const mapped = mapFetchedData(fetchedData);
      setData(prev => ({ ...prev, ...mapped }));

      // 3. Save to localStorage
      writeLocalCache(fetchedData);
    } catch (error) {
      console.error("❌ Failed to load initial data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isSecretMode, mapFetchedData]);

  // ================================================================
  // FORCE REFRESH (used after writes)
  // ================================================================
  const forceRefresh = useCallback(async () => {
    clearLocalCache();
    await loadInitialData({ forceRefresh: true });
  }, [loadInitialData]);

  // ================================================================
  // WRITE WRAPPER — auto-invalidate + refresh
  // ================================================================
  // const wrapWrite = useCallback(async (fn) => {
  //   try {
  //     const result = await fn();
  //     if (result?.success) {
  //       clearLocalCache();
  //       loadInitialData({ forceRefresh: true }).catch(e => console.warn("Post-write refresh failed:", e));
  //     }
  //     return result;
  //   } catch (e) {
  //     console.error("Write failed:", e);
  //     return { success: false, error: e.message };
  //   }
  // }, [loadInitialData]);

  // ================================================================
  // INITIAL LOAD + SECRET MODE CHANGE
  // ================================================================
  useEffect(() => {
    initialLoadRef.current = false;

    const loadData = async () => {
      if (initialLoadRef.current) return;
      initialLoadRef.current = true;
      await loadInitialData();
      console.log("Data reloaded due to secret mode change:", isSecretMode);
    };

    loadData();

    return () => {
      initialLoadRef.current = false;
    };
  }, [isSecretMode, loadInitialData]);

  // ================================================================
  // REFRESH ON TAB FOCUS (if cache > 60s old)
  // ================================================================
  useEffect(() => {
    const onFocus = () => {
      const ts = parseInt(localStorage.getItem(LS_TIMESTAMP_KEY) || "0", 10);
      if (Date.now() - ts > 60000) {
        console.log("🔄 Tab focused, cache stale — refreshing");
        loadInitialData();
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadInitialData]);

  // ================================================================
  // STOCK HELPERS
  // ================================================================
  const markStockAsSold = useCallback(async (stockId, soldDetails) => {
    setLoading(true);
    try {
      const stockItem = data.Stock.find(item => item.id === stockId);
      if (!stockItem) {
        throw new Error("Stock item not found");
      }

      const soldDescription = {
        sold_date: soldDetails.date || new Date().toISOString().split('T')[0],
        sold_bill_no: soldDetails.billNo || "N/A",
        customer_id: soldDetails.customerId,
        transaction_id: soldDetails.transactionId,
        customer_name: soldDetails.customerName,
        ...soldDetails.additionalDetails
      };

      const updatedData = {
        ...stockItem,
        Sold: "Yes",
        SoldDescription: JSON.stringify(soldDescription)
      };

      setData(prev => ({
        ...prev,
        Stock: prev.Stock.map(item =>
          item.id === stockId ? { ...item, ...updatedData } : item
        )
      }));

      const response = await updateStock(stockId, updatedData);

      if (response?.success) {
        clearLocalCache();
        return { success: true, message: "Stock marked as sold successfully" };
      } else {
        setData(prev => ({
          ...prev,
          Stock: prev.Stock.map(item =>
            item.id === stockId ? stockItem : item
          )
        }));
        throw new Error(response?.error || "Failed to update stock on server");
      }
    } catch (error) {
      console.error("Error marking stock as sold:", error);
      return { success: false, error: error.message, message: "Failed to mark stock as sold" };
    } finally {
      setLoading(false);
    }
  }, [data.Stock]);

  const getSoldItems = useCallback(() => {
    return data.Stock.filter(item => item.Sold === "Yes");
  }, [data.Stock]);

  const getSoldDescription = useCallback((stockId) => {
    const item = data.Stock.find(item => item.id === stockId);
    if (!item || item.Sold !== "Yes") return null;
    try {
      return typeof item.SoldDescription === 'string'
        ? JSON.parse(item.SoldDescription)
        : item.SoldDescription;
    } catch (e) {
      console.error("Error parsing sold description:", e);
      return null;
    }
  }, [data.Stock]);

  // ================================================================
  // ADVANCE PAYMENT FUNCTIONS
  // ================================================================
  const withdrawAdvance = async (withdrawData) => {
    setLoading(true);
    try {
      const { customerId, withdrawalModes, totalAmount, reason, paymentMode } = withdrawData;

      let usageAmounts = withdrawalModes;

      if (!usageAmounts && paymentMode && totalAmount) {
        usageAmounts = {};
        if (paymentMode === 'cash') usageAmounts.cash = totalAmount;
        else if (paymentMode === 'online') usageAmounts.online = totalAmount;
        else if (paymentMode === 'oldGold') usageAmounts.oldGold = totalAmount;
        else if (paymentMode === 'oldSilver') usageAmounts.oldSilver = totalAmount;
        else usageAmounts.cash = totalAmount;
      }

      if (!customerId) throw new Error('Customer ID is required');
      if (!usageAmounts || Object.keys(usageAmounts).length === 0) throw new Error('At least one withdrawal mode is required');
      if (!reason || !reason.trim()) throw new Error('Reason for withdrawal is required');

      const withdrawalBillId = `WITHDRAW`;
      const withdrawalBillNo = reason;

      const response = await advanceUseForBill(
        customerId,
        withdrawalBillId,
        withdrawalBillNo,
        usageAmounts,
        totalAmount
      );

      if (response?.success) {
        clearLocalCache();
        await loadInitialData({ forceRefresh: true });
        return {
          success: true,
          data: response.data,
          message: `₹${totalAmount} withdrawn successfully from advance balance. Reason: ${reason}`
        };
      } else {
        throw new Error(response?.error || "Failed to withdraw advance");
      }
    } catch (error) {
      console.error("Error withdrawing advance:", error);
      return { success: false, error: error.message, message: "Failed to withdraw advance" };
    } finally {
      setLoading(false);
    }
  };

  const getCustomerAdvanceHistory = async (customerId) => {
    try {
      const result = await getCustomerAdvancePayments(customerId);

      if (result.success && result.data) {
        const history = [];

        result.data.forEach(advance => {
          if (advance.totalAmount > 0) {
            history.push({
              type: "add",
              date: new Date(advance.date).toLocaleDateString(),
              amount: advance.totalAmount,
              paymentBreakdown: advance.paymentModes,
              paymentMode: Object.keys(advance.paymentModes).join(", "),
              remarks: advance.remarks,
              id: advance.id,
              created_at: advance.created_at
            });
          }

          let usageRecords = [];
          if (advance.remainingBalances && Array.isArray(advance.remainingBalances)) {
            usageRecords = advance.remainingBalances;
          } else if (advance.usedInBills && Array.isArray(advance.usedInBills)) {
            usageRecords = advance.usedInBills;
          }

          if (usageRecords.length > 0) {
            usageRecords.forEach(use => {
              history.push({
                type: "used",
                date: new Date(use.date).toLocaleDateString(),
                amount: use.amountUsed,
                paymentBreakdown: use.modeBreakdown,
                remarks: `Used in Bill #${use.billNo || use.billId}`,
                billId: use.billId,
                billNo: use.billNo
              });
            });
          }
        });

        history.sort((a, b) => new Date(b.date) - new Date(a.date));
        return history;
      }

      return [];
    } catch (error) {
      console.error("Error getting advance history:", error);
      return [];
    }
  };

  const addAdvancePayment = async (paymentData) => {
    setLoading(true);
    try {
      const { customerId, customerName, paymentModes, totalAmount, date, remarks, recordedBy } = paymentData;

      const response = await apiAddAdvancePayment({
        customerId,
        customerName,
        paymentModes,
        totalAmount,
        date: date ? new Date(date).toISOString() : new Date().toISOString(),
        remarks: remarks || "",
        recordedBy: recordedBy || currentUser?.name || "System"
      });

      if (response?.success) {
        clearLocalCache();
        await loadInitialData({ forceRefresh: true });
        return {
          success: true,
          data: response.data,
          message: `Advance payment of ₹${totalAmount} recorded successfully`
        };
      } else {
        throw new Error(response?.error || "Failed to record advance payment");
      }
    } catch (error) {
      console.error("Error adding advance payment:", error);
      return { success: false, error: error.message, message: "Failed to record advance payment" };
    } finally {
      setLoading(false);
    }
  };

  const recordAdvancePayment = async (paymentData) => {
    setLoading(true);
    try {
      const result = await addAdvancePayment(paymentData);

      if (result.success) {
        setData(prev => {
          const updatedCustomers = prev.Customers.map(customer => {
            if (customer.id === paymentData.customerId) {
              return {
                ...customer,
                totalAdvanceBalance: (customer.totalAdvanceBalance || 0) + paymentData.totalAmount
              };
            }
            return customer;
          });
          return { ...prev, Customers: updatedCustomers };
        });
        return result;
      }
      throw new Error(result.error || "Failed to record advance payment");
    } catch (error) {
      console.error("Error recording advance payment:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerAdvanceBalance = useCallback(async (customerId) => {
    setLoading(true);
    try {
      const result = await getCustomerAdvancePayments(customerId);

      if (result.success && result.data) {
        let totalRemaining = 0;
        const advancesByMode = { cash: 0, online: 0, cheque: 0, oldGold: 0, oldSilver: 0, other: 0 };

        result.data.forEach(advance => {
          if (advance.status === 'active' || advance.status === 'partially_used') {
            let remainingBalances = {};
            if (advance.remainingBalances && typeof advance.remainingBalances === 'object') {
              if (Array.isArray(advance.remainingBalances)) {
                remainingBalances = advance.paymentModes || {};
              } else {
                remainingBalances = advance.remainingBalances;
              }
            } else {
              remainingBalances = advance.paymentModes || {};
            }

            Object.keys(remainingBalances).forEach(mode => {
              const amount = parseFloat(remainingBalances[mode]) || 0;
              totalRemaining += amount;

              let mappedMode = mode;
              if (mode === 'oldGold') mappedMode = 'oldGold';
              else if (mode === 'oldSilver') mappedMode = 'oldSilver';
              else if (mode === 'online') mappedMode = 'online';
              else if (mode === 'cheque') mappedMode = 'cheque';
              else if (mode === 'cash') mappedMode = 'cash';
              else mappedMode = 'other';

              if (advancesByMode[mappedMode] !== undefined) {
                advancesByMode[mappedMode] += amount;
              } else {
                advancesByMode.other += amount;
              }
            });
          }
        });

        return { total: totalRemaining, byMode: advancesByMode, advances: result.data };
      }

      return { total: 0, byMode: { cash: 0, online: 0, cheque: 0, oldGold: 0, oldSilver: 0, other: 0 }, advances: [] };
    } catch (error) {
      console.error("Error fetching advance balance:", error);
      return { total: 0, byMode: { cash: 0, online: 0, cheque: 0, oldGold: 0, oldSilver: 0, other: 0 }, advances: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  const applyAdvanceToBill = async (customerId, billId, billNo, usageAmounts, totalAmount) => {
    setLoading(true);
    try {
      const result = await advanceUseForBill(customerId, billId, billNo, usageAmounts, totalAmount);

      if (result.success) {
        clearLocalCache();
        setData(prev => {
          const updatedCustomers = prev.Customers.map(customer => {
            if (customer.id === customerId) {
              const newBalance = (customer.totalAdvanceBalance || 0) - totalAmount;
              return { ...customer, totalAdvanceBalance: newBalance > 0 ? newBalance : 0 };
            }
            return customer;
          });
          return { ...prev, Customers: updatedCustomers };
        });
        return result;
      }
      throw new Error(result.error || "Failed to apply advance");
    } catch (error) {
      console.error("Error applying advance to bill:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const getAllAdvancePaymentsList = async (filters = {}) => {
    setLoading(true);
    try {
      return await getAllAdvancePayments(filters);
    } catch (error) {
      console.error("Error fetching advance payments:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const getAdvanceSummary = async (customerId) => {
    setLoading(true);
    try {
      return await getAdvancePaymentSummary(customerId);
    } catch (error) {
      console.error("Error fetching advance summary:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // ================================================================
  // COMPANY DETAILS
  // ================================================================
  const getCompanyDetailsFromContext = useCallback((filters = {}) => {
    let companies = data.CompanyDetails;

    if (filters.filterBy && filters.filterValue) {
      companies = companies.filter(company =>
        String(company[filters.filterBy] || '').toLowerCase().includes(String(filters.filterValue).toLowerCase())
      );
    }

    return companies;
  }, [data.CompanyDetails]);

  const updateCompanyDetailsInContext = async (id, companyData) => {
    setLoading(true);
    try {
      const response = await updateCompanyDetails(id, companyData);

      if (response?.success === true) {
        const updatedCompany = response.data;
        setData(prev => ({
          ...prev,
          CompanyDetails: prev.CompanyDetails.map(company =>
            company.id === id ? { ...company, ...updatedCompany } : company
          )
        }));

        if (currentUser?.id === id) {
          sessionManager.updateSession({ ...currentUser, ...companyData });
          setCurrentUser(sessionManager.getSession());
        }

        clearLocalCache();
        return { success: true, data: updatedCompany, message: "Company details updated successfully" };
      } else {
        throw new Error(response?.error || "Failed to update company details");
      }
    } catch (error) {
      console.error("Error updating company details:", error);
      return { success: false, error: error.message, message: "Failed to update company details" };
    } finally {
      setLoading(false);
    }
  };

  const addCompanyDetailsToContext = async (companyData) => {
    setLoading(true);
    try {
      const response = await apiAddCompanyDetails(companyData);

      if (response?.success === true) {
        const newCompany = response.data;
        setData(prev => ({
          ...prev,
          CompanyDetails: [...prev.CompanyDetails, newCompany]
        }));
        clearLocalCache();
        return { success: true, data: newCompany, message: "Company details added successfully" };
      } else {
        throw new Error(response?.error || "Failed to add company details");
      }
    } catch (error) {
      console.error("Error adding company details:", error);
      return { success: false, error: error.message, message: "Failed to add company details" };
    } finally {
      setLoading(false);
    }
  };

  // ================================================================
  // GROSS STOCK
  // ================================================================
  const getGrossStockFromContext = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const result = await apiGetGrossStock(filters);
      setData(prev => ({ ...prev, GrossStock: result }));
      return result;
    } catch (error) {
      console.error('Error getting gross stock:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const addGrossStockItemToContext = async (itemData) => {
    setLoading(true);
    try {
      const result = await apiAddGrossStockItem(itemData);
      if (result) {
        setData(prev => ({ ...prev, GrossStock: [...(prev.GrossStock || []), result] }));
        clearLocalCache();
        return { success: true, data: result, message: "Item added to Gross Stock successfully" };
      }
      throw new Error("Failed to add item");
    } catch (error) {
      console.error('Error adding gross stock item:', error);
      return { success: false, error: error.message, message: "Failed to add item to Gross Stock" };
    } finally {
      setLoading(false);
    }
  };

  const addStockTransactionToContext = async (transactionData) => {
    setLoading(true);
    try {
      const result = await apiAddStockTransaction(transactionData);

      if (result) {
        const updatedStock = result.updatedStock;
        const newTransaction = {
          id: result.id,
          itemId: transactionData.itemId,
          operation: transactionData.operation,
          numberOfPieces: transactionData.numberOfPieces || 0,
          weightChanged: transactionData.weightChanged || 0,
          partyName: transactionData.partyName || '',
          description: transactionData.description || '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        setData(prev => {
          const updatedGrossStock = (prev.GrossStock || []).map(item => {
            if (item.id === transactionData.itemId) {
              return {
                ...item,
                currentStock: updatedStock.pieces,
                currentWeight: updatedStock.weight,
                updated_at: new Date().toISOString()
              };
            }
            return item;
          });

          return {
            ...prev,
            GrossStock: updatedGrossStock,
            GrossStockTransactions: [newTransaction, ...(prev.GrossStockTransactions || [])]
          };
        });

        clearLocalCache();
        return { success: true, data: result, message: "Stock transaction added successfully" };
      }
      throw new Error("Failed to add stock transaction");
    } catch (error) {
      console.error('Error adding stock transaction:', error);
      return { success: false, error: error.message, message: "Failed to add stock transaction" };
    } finally {
      setLoading(false);
    }
  };

  const getGrossStockTransactionsFromContext = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const result = await apiGetStockTransactions(filters);
      setData(prev => ({ ...prev, GrossStockTransactions: result }));
      return result;
    } catch (error) {
      console.error('Error getting gross stock transactions:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getStockSummaryFromContext = useCallback(async (options = {}) => {
    setLoading(true);
    try {
      return await apiGetStockSummary(options);
    } catch (error) {
      console.error('Error getting stock summary:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getCurrentStockFromContext = useCallback(async (itemId) => {
    setLoading(true);
    try {
      return await apiGetCurrentStock(itemId);
    } catch (error) {
      console.error('Error getting current stock:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateStockTransactionInContext = async (transactionId, updateData) => {
    setLoading(true);
    try {
      const result = await apiUpdateStockTransaction(transactionId, updateData);

      if (result) {
        setData(prev => {
          const updatedTransactions = (prev.GrossStockTransactions || []).map(tx => {
            if (tx.id === transactionId) {
              return { ...tx, ...updateData, updated_at: new Date().toISOString() };
            }
            return tx;
          });

          let updatedGrossStock = prev.GrossStock || [];
          if (result.updatedStock) {
            updatedGrossStock = updatedGrossStock.map(item => {
              if (item.id === (updateData.itemId || result.itemId)) {
                return {
                  ...item,
                  currentStock: result.updatedStock.pieces,
                  currentWeight: result.updatedStock.weight,
                  updated_at: new Date().toISOString()
                };
              }
              return item;
            });
          }

          return {
            ...prev,
            GrossStock: updatedGrossStock,
            GrossStockTransactions: updatedTransactions
          };
        });

        clearLocalCache();
        return { success: true, data: result, message: "Transaction updated successfully" };
      }
      throw new Error("Failed to update transaction");
    } catch (error) {
      console.error('Error updating stock transaction:', error);
      return { success: false, error: error.message, message: "Failed to update transaction" };
    } finally {
      setLoading(false);
    }
  };

  const deleteStockTransactionFromContext = async (transactionId) => {
    setLoading(true);
    try {
      const result = await apiDeleteStockTransaction(transactionId);

      if (result) {
        setData(prev => {
          const filteredTransactions = (prev.GrossStockTransactions || []).filter(tx => tx.id !== transactionId);

          let updatedGrossStock = prev.GrossStock || [];
          if (result.updatedStock && result.itemId) {
            updatedGrossStock = updatedGrossStock.map(item => {
              if (item.id === result.itemId) {
                return {
                  ...item,
                  currentStock: result.updatedStock.pieces,
                  currentWeight: result.updatedStock.weight,
                  updated_at: new Date().toISOString()
                };
              }
              return item;
            });
          }

          return {
            ...prev,
            GrossStock: updatedGrossStock,
            GrossStockTransactions: filteredTransactions
          };
        });

        clearLocalCache();
        return { success: true, data: result, message: "Transaction deleted successfully" };
      }
      throw new Error("Failed to delete transaction");
    } catch (error) {
      console.error('Error deleting stock transaction:', error);
      return { success: false, error: error.message, message: "Failed to delete transaction" };
    } finally {
      setLoading(false);
    }
  };

  const bulkAddStockTransactionsToContext = async (transactions) => {
    setLoading(true);
    try {
      const result = await apiBulkAddStockTransactions(transactions);

      if (result) {
        await getGrossStockFromContext();
        await getGrossStockTransactionsFromContext();
        clearLocalCache();
        return { success: true, data: result, message: "Bulk transactions added successfully" };
      }
      throw new Error("Failed to add bulk transactions");
    } catch (error) {
      console.error('Error adding bulk stock transactions:', error);
      return { success: false, error: error.message, message: "Failed to add bulk transactions" };
    } finally {
      setLoading(false);
    }
  };

  const getStockByMetalFromContext = useCallback(async (metal) => {
    setLoading(true);
    try {
      return await apiGetStockByMetal(metal);
    } catch (error) {
      console.error('Error getting stock by metal:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const searchItemsFromContext = useCallback(async (searchTerm) => {
    setLoading(true);
    try {
      return await apiSearchItems(searchTerm);
    } catch (error) {
      console.error('Error searching items:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getItemTransactionHistoryFromContext = useCallback(async (itemId, options = {}) => {
    setLoading(true);
    try {
      return await apiGetItemTransactionHistory(itemId, options);
    } catch (error) {
      console.error('Error getting item transaction history:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getStockValueSummaryFromContext = useCallback(async (metal = null) => {
    setLoading(true);
    try {
      return await apiGetStockValueSummary(metal);
    } catch (error) {
      console.error('Error getting stock value summary:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // ================================================================
  // AUTHENTICATION
  // ================================================================
  const loginInContext = async (email, password) => {
    setLoading(true);
    try {
      const response = await apiLogin(email, password);

      if (response?.success === true) {
        const userData = response.data;
        sessionManager.createSession({
          id: userData.id,
          email: userData.email,
          name: userData.shopName || userData.ownerName,
          shopName: userData.shopName,
          ownerName: userData.ownerName
        });

        setCurrentUser(sessionManager.getSession());
        setIsAuthenticated(true);

        return { success: true, data: userData, message: "Login successful" };
      } else {
        throw new Error(response?.error || "Login failed");
      }
    } catch (error) {
      console.error("Error logging in:", error);
      return { success: false, error: error.message, message: "Login failed" };
    } finally {
      setLoading(false);
    }
  };

  const logoutInContext = () => {
    sessionManager.clearSession();
    setCurrentUser(null);
    setIsAuthenticated(false);
    clearLocalCache();
    return { success: true, message: "Logged out successfully" };
  };

  const sendPasswordResetOtpInContext = async (email) => {
    setLoading(true);
    try {
      return await sendPasswordResetOtp(email);
    } catch (error) {
      console.error("Error sending password reset OTP:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const verifyPasswordResetOtpInContext = async (email, otp) => {
    setLoading(true);
    try {
      return await verifyPasswordResetOtp(email, otp);
    } catch (error) {
      console.error("Error verifying password reset OTP:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const resetPasswordInContext = async (email, newPassword) => {
    setLoading(true);
    try {
      return await resetPassword(email, newPassword);
    } catch (error) {
      console.error("Error resetting password:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const updatePasswordInContext = async (email, password) => {
    setLoading(true);
    try {
      const response = await apiUpdatePassword(email, password);

      if (response?.success === true) {
        const userData = response.data;
        sessionManager.createSession({
          id: userData.id,
          email: userData.email,
          name: userData.shopName || userData.ownerName
        });
        setCurrentUser(sessionManager.getSession());
        return { success: true, data: userData, message: "Password updated successfully" };
      } else {
        throw new Error(response?.error || "Failed to update password");
      }
    } catch (error) {
      console.error("Error updating password:", error);
      return { success: false, error: error.message, message: "Failed to update password" };
    } finally {
      setLoading(false);
    }
  };

  // ================================================================
  // ROZNAMA
  // ================================================================
  const closeRoznamaDayInContext = async (date, openingBalanceCash, openingBalanceBank, incomeTransactions, expenseTransactions, closingBalanceBank, closingBalanceCash, netProfit, totalIncome, totalExpense) => {
    setLoading(true);
    try {
      const response = await closeRoznamaDay(date, openingBalanceCash, openingBalanceBank, incomeTransactions, expenseTransactions, closingBalanceBank, closingBalanceCash, netProfit, totalIncome, totalExpense);

      if (response?.success) {
        clearLocalCache();
        await loadInitialData({ forceRefresh: true });
        return { success: true, data: response.data, message: "Day closed successfully" };
      } else {
        throw new Error(response?.error || "Failed to close day");
      }
    } catch (error) {
      console.error("Error closing roznama day:", error);
      return { success: false, error: error.message, message: "Failed to close day" };
    } finally {
      setLoading(false);
    }
  };

  const addRoznamaToContext = async (roznamaData) => {
    setLoading(true);
    try {
      const completeRoznamaData = {
        ...roznamaData,
        closingBalanceCash: roznamaData.closingBalanceCash !== undefined
          ? roznamaData.closingBalanceCash
          : (parseFloat(roznamaData.openingBalanceCash) || 0) +
          (calculateTotal(roznamaData.incomeTransactions || [], 'cash') || 0) -
          (calculateTotal(roznamaData.expenseTransactions || [], 'cash') || 0),
        closingBalanceBank: roznamaData.closingBalanceBank !== undefined
          ? roznamaData.closingBalanceBank
          : (parseFloat(roznamaData.openingBalanceBank) || 0) +
          (calculateTotal(roznamaData.incomeTransactions || [], 'bank') || 0) -
          (calculateTotal(roznamaData.expenseTransactions || [], 'bank') || 0),
        netProfit: roznamaData.netProfit !== undefined
          ? roznamaData.netProfit
          : (calculateTotal(roznamaData.incomeTransactions || []) || 0) -
          (calculateTotal(roznamaData.expenseTransactions || []) || 0),
        totalIncome: roznamaData.totalIncome !== undefined
          ? roznamaData.totalIncome
          : calculateTotal(roznamaData.incomeTransactions || []) || 0,
        totalExpense: roznamaData.totalExpense !== undefined
          ? roznamaData.totalExpense
          : calculateTotal(roznamaData.expenseTransactions || []) || 0,
      };

      const response = await addRoznamaDay(completeRoznamaData);

      if (response?.success) {
        const newRoznama = response.data;
        setData(prev => ({
          ...prev,
          Roznama: [...prev.Roznama, newRoznama]
        }));
        clearLocalCache();
        return { success: true, data: newRoznama, message: "Roznama added successfully" };
      } else {
        throw new Error(response?.error || "Failed to add roznama");
      }
    } catch (error) {
      console.error("Error adding roznama:", error);
      return { success: false, error: error.message, message: "Failed to add roznama" };
    } finally {
      setLoading(false);
    }
  };

  const findRoznamaByDate = useCallback((date) => {
    const roznamaArray = data.Roznama || [];
    return roznamaArray.find((r) => {
      try {
        const roznamaDate = new Date(r.date);
        const searchDate = new Date(date);
        return roznamaDate.toISOString().split('T')[0] === searchDate.toISOString().split('T')[0];
      } catch (error) {
        return false;
      }
    });
  }, [data.Roznama]);

  const updateRoznamaInContext = async (id, roznamaData) => {
    setLoading(true);
    try {
      const completeRoznamaData = {
        ...roznamaData,
        closingBalanceCash: roznamaData.closingBalanceCash !== undefined
          ? roznamaData.closingBalanceCash
          : (roznamaData.openingBalanceCash || 0) +
          (calculateTotal(roznamaData.incomeTransactions || [], 'cash') || 0) -
          (calculateTotal(roznamaData.expenseTransactions || [], 'cash') || 0),
        closingBalanceBank: roznamaData.closingBalanceBank !== undefined
          ? roznamaData.closingBalanceBank
          : (roznamaData.openingBalanceBank || 0) +
          (calculateTotal(roznamaData.incomeTransactions || [], 'bank') || 0) -
          (calculateTotal(roznamaData.expenseTransactions || [], 'bank') || 0),
        netProfit: roznamaData.netProfit !== undefined
          ? roznamaData.netProfit
          : (calculateTotal(roznamaData.incomeTransactions || []) || 0) -
          (calculateTotal(roznamaData.expenseTransactions || []) || 0),
        totalIncome: roznamaData.totalIncome !== undefined
          ? roznamaData.totalIncome
          : calculateTotal(roznamaData.incomeTransactions || []) || 0,
        totalExpense: roznamaData.totalExpense !== undefined
          ? roznamaData.totalExpense
          : calculateTotal(roznamaData.expenseTransactions || []) || 0,
      };

      const response = await updateRoznamaDay(id, completeRoznamaData);

      if (response?.success) {
        const updatedRoznama = response.data;
        setData(prev => ({
          ...prev,
          Roznama: prev.Roznama.map(item =>
            item.id === id ? { ...item, ...updatedRoznama } : item
          )
        }));
        clearLocalCache();
        return { success: true, data: updatedRoznama, message: "Roznama updated successfully" };
      } else {
        throw new Error(response?.error || "Failed to update roznama");
      }
    } catch (error) {
      console.error("Error updating roznama:", error);
      return { success: false, error: error.message, message: "Failed to update roznama" };
    } finally {
      setLoading(false);
    }
  };

  const addOrUpdateRoznamaInContext = async (roznamaData) => {
    setLoading(true);
    try {
      const existingRoznama = data.Roznama.find(r => {
        try {
          const rDate = new Date(r.date).toISOString().split('T')[0];
          const newDate = new Date(roznamaData.date).toISOString().split('T')[0];
          return rDate === newDate;
        } catch (error) {
          return false;
        }
      });

      if (existingRoznama) {
        return await updateRoznamaInContext(existingRoznama.id, roznamaData);
      } else {
        return await addRoznamaToContext(roznamaData);
      }
    } catch (error) {
      console.error("Error in addOrUpdateRoznama:", error);
      return { success: false, error: error.message, message: "Failed to save roznama" };
    } finally {
      setLoading(false);
    }
  };

  const deleteRoznamaFromContext = (id) =>
    deleteItem('Roznama', id, deleteRoznamaDay);

  // ================================================================
  // GENERIC CRUD
  // ================================================================
  const updateData = useCallback((dataType, newData) => {
    setData(prev => ({ ...prev, [dataType]: newData }));
  }, []);

  const createItem = async (dataType, itemData, serviceFunction) => {
    setLoading(true);
    try {
      const response = await serviceFunction(itemData);

      if (response?.success) {
        const newItem = response.data;
        if (dataType === 'EInvoices') {
          newItem.billNo = Number(newItem.billNo);
        }

        setData(prev => {
          const updatedArray = [...prev[dataType], newItem];
          const finalArray = dataType === 'Customers' || dataType === 'Suppliers'
            ? sortByName(updatedArray)
            : updatedArray;

          return { ...prev, [dataType]: finalArray };
        });

        clearLocalCache();
        return { success: true, data: newItem, message: `${getDataTypeLabel(dataType)} added successfully` };
      } else {
        throw new Error(response?.error || `Failed to add ${dataType}`);
      }
    } catch (error) {
      console.error(`Error adding ${dataType}:`, error);
      return { success: false, error: error.message, message: `Failed to add ${getDataTypeLabel(dataType)}` };
    } finally {
      setLoading(false);
    }
  };

  const updateItem = async (dataType, id, itemData, serviceFunction) => {
    setLoading(true);
    try {
      const response = await serviceFunction(id, itemData);

      if (response?.success) {
        const updatedItem = response.data;
        setData(prev => {
          const updatedArray = prev[dataType].map(item =>
            item.id === id ? { ...item, ...updatedItem } : item
          );
          const finalArray = dataType === 'Customers' || dataType === 'Suppliers'
            ? sortByName(updatedArray)
            : updatedArray;

          return { ...prev, [dataType]: finalArray };
        });

        clearLocalCache();
        return { success: true, data: updatedItem, message: `${getDataTypeLabel(dataType)} updated successfully` };
      } else {
        throw new Error(response?.error || `Failed to update ${dataType}`);
      }
    } catch (error) {
      console.error(`Error updating ${dataType}:`, error);
      return { success: false, error: error.message, message: `Failed to update ${getDataTypeLabel(dataType)}` };
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (dataType, id, serviceFunction) => {
    setLoading(true);
    try {
      const response = await serviceFunction(id);

      if (response?.success) {
        setData(prev => {
          const updatedArray = prev[dataType].filter(item => item.id !== id);
          return { ...prev, [dataType]: updatedArray };
        });

        clearLocalCache();
        return { success: true, message: `${getDataTypeLabel(dataType)} deleted successfully` };
      } else {
        throw new Error(response?.error || `Failed to delete ${dataType}`);
      }
    } catch (error) {
      console.error(`Error deleting ${dataType}:`, error);
      return { success: false, error: error.message, message: `Failed to delete ${getDataTypeLabel(dataType)}` };
    } finally {
      setLoading(false);
    }
  };

  const getDataByType = useCallback((dataType, filterFn) => {
    const dataArray = data[dataType] || [];
    return filterFn ? dataArray.filter(filterFn) : dataArray;
  }, [data]);

  const addDataType = useCallback((dataType, initialValue = []) => {
    if (!data[dataType]) {
      setData(prev => ({ ...prev, [dataType]: initialValue }));
    }
  }, [data]);

  // ================================================================
  // CUSTOMER / URD / SCHEME CRUD
  // ================================================================
  const addCustomerToContext = (customerData) => createItem('Customers', customerData, addCustomer);
  const updateCustomerInContext = (id, customerData) => updateItem('Customers', id, customerData, updateCustomer);
  const deleteCustomerFromContext = (id) => deleteItem('Customers', id, deleteCustomer);

  const addUrdPurchaseToContext = (urdPurchaseData) => createItem('URDPurchase', urdPurchaseData, addUrdPurchase);
  const updateUrdPurchaseInContext = (id, urdPurchaseData) => updateItem('URDPurchase', id, urdPurchaseData, updateUrdPurchase);
  const deleteUrdPurchaseFromContext = (id) => deleteItem('URDPurchase', id, deleteUrdPurchase);

  // ================================================================
  // E-INVOICE CRUD
  // ================================================================
  const addEInvoiceToContext = (invoiceData) =>
    createItem("EInvoices", invoiceData, apiAddEInvoice);
  const updateEInvoiceInContext = (id, invoiceData) =>
    updateItem("EInvoices", id, invoiceData, apiUpdateEInvoice);
  const deleteEInvoiceFromContext = (id) =>
    deleteItem("EInvoices", id, apiDeleteEInvoice);

  const addSchemeCardToContext = (schemeCardData) => createItem('SchemeCard', schemeCardData, addSchemeCard);
  const updateSchemeCardInContext = (id, schemeCardData) => updateItem('SchemeCard', id, schemeCardData, updateSchemeCard);
  const deleteSchemeCardFromContext = (id) => deleteItem('SchemeCard', id, deleteSchemeCard);

  // ================================================================
  // CUSTOMER TRANSACTIONS
  // ================================================================
  const addCustomerTransactionToContext = (transactionData) => createItem('CustomerTransactions', transactionData, addCustomerTransaction);
  const updateCustomerTransactionInContext = (id, transactionData) => updateItem('CustomerTransactions', id, transactionData, updateCustomerTransaction);
  const deleteCustomerTransactionFromContext = (id) => deleteItem('CustomerTransactions', id, deleteCustomerTransaction);

  // ================================================================
  // COMBINED: addCustomerTransactionAndUpdateStock
  // ================================================================
  const addCustomerTransactionAndUpdateStock = async (transactionData, stockItems) => {
    setLoading(true);
    try {
      const response = await apiAddCustomerTransactionAndUpdateStock(transactionData, stockItems);

      if (response?.success) {
        const newTransactionId = response?.data?.id;
        const customerUpdateInfo = response?.data?.customerUpdate;
        const timestamps = response?.data?.timestamps;
        const grossStockDeductions = response?.data?.grossStockDeductions;
        const currentTime = new Date().toISOString();

        const newTransaction = {
          id: newTransactionId,
          customer: transactionData.customer,
          date: transactionData.date.toString().charAt(0) === "'" ? transactionData?.date?.toString().slice(1) : transactionData.date.toString(),
          items: transactionData.items,
          calculatedTotals: transactionData.calculatedTotals,
          paymentDetails: {
            ...transactionData.paymentDetails,
            paymentHistory: [{
              id: generatePaymentId(),
              date: new Date().toISOString(),
              amount: transactionData.paymentDetails.totalPaymentDone || 0,
              paymentModes: {
                cash: transactionData.paymentDetails.cash || 0,
                online: transactionData.paymentDetails.online || 0,
                cheque: transactionData.paymentDetails.cheque || 0,
                other: transactionData.paymentDetails.other || 0,
                advanceBreakup: transactionData.paymentDetails.advancePayment?.breakup || {}
              },
              note: "Initial payment",
              recordedBy: transactionData.metaDetails?.handledBy || "System"
            }]
          },
          summary: {
            ...transactionData.summary,
            pendingAmount: transactionData.summary.pendingAmount || 0
          },
          transactionDetails: {
            ...transactionData.transactionDetails,
            originalBillAmount: transactionData.summary.grandTotal,
            balanceDue: transactionData.summary.pendingAmount || 0,
            isFullyPaid: (transactionData.summary.pendingAmount || 0) === 0,
            paymentStatus: (transactionData.summary.pendingAmount || 0) === 0 ? "paid" : "partial"
          },
          metaDetails: transactionData.metaDetails,
          created_at: timestamps?.created_at || currentTime,
          updated_at: timestamps?.updated_at || currentTime
        };

        const convertToYYYYMMDD = (dateInput) => {
          try {
            if (!dateInput && dateInput !== 0) return null;
            let dateStr = String(dateInput).trim();
            if (dateStr.startsWith("'")) dateStr = dateStr.substring(1);

            if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;

            let date;
            if (dateStr.includes('/')) {
              const parts = dateStr.split('/');
              if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const year = parseInt(parts[2], 10);
                date = new Date(year, month, day);
                if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
                  throw new Error('Invalid date after parsing');
                }
              } else {
                throw new Error(`Invalid date format: ${dateStr}`);
              }
            } else {
              date = new Date(dateStr);
            }

            if (isNaN(date.getTime())) throw new Error(`Invalid date: ${dateStr}`);

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          } catch (error) {
            return null;
          }
        };

        setData(prev => {
          const updatedTransactions = [...prev.CustomerTransactions, newTransaction];

          const updatedStock = prev.Stock.map(stockItem => {
            if (stockItems.includes(stockItem.id)) {
              const soldDescription = {
                sold_date: transactionData.date,
                sold_bill_no: transactionData.transactionDetails?.billNo || 0,
                customer_id: transactionData.customer,
                transaction_id: newTransactionId,
                customer_name: transactionData.customerName || "Unknown Customer"
              };

              return {
                ...stockItem,
                sold: "Yes",
                soldDescription: soldDescription,
                updated_at: currentTime
              };
            }
            return stockItem;
          });

          let updatedCustomers = prev.Customers;
          if (customerUpdateInfo?.customerId) {
            updatedCustomers = prev.Customers.map(customer => {
              if (customer.id === customerUpdateInfo.customerId) {
                return {
                  ...customer,
                  currentBalance: customerUpdateInfo.newBalance || 0,
                  updated_at: currentTime
                };
              }
              return customer;
            });
          }

          let updatedGrossStock = [...(prev.GrossStock || [])];
          if (grossStockDeductions?.results && grossStockDeductions.results.length > 0) {
            grossStockDeductions.results.forEach(deduction => {
              if (deduction.success) {
                const itemIndex = updatedGrossStock.findIndex(item => item.id === deduction.itemId);
                if (itemIndex !== -1) {
                  updatedGrossStock[itemIndex] = {
                    ...updatedGrossStock[itemIndex],
                    currentStock: deduction.newStock,
                    currentWeight: deduction.newWeight,
                    updated_at: currentTime
                  };
                }
              }
            });
          }

          let updatedRoznama = [...prev.Roznama];
          const customerName = prev.Customers.find(c => c.id === transactionData.customer)?.name || "Unknown Customer";

          const calculateRoznamaTransactionFromCustomer = (transactionData, customerName) => {
            const cashPayment = parseFloat(transactionData.paymentDetails?.cash || 0) +
              parseFloat(transactionData.paymentDetails?.advancePayment?.breakup?.cash || 0);

            const bankPayment =
              parseFloat(transactionData.paymentDetails?.cheque || 0) +
              parseFloat(transactionData.paymentDetails?.online || 0) +
              parseFloat(transactionData.paymentDetails?.advancePayment?.breakup?.online || 0);

            const paymentMode = bankPayment === 0 && cashPayment > 0 ? "cash" :
              bankPayment > 0 && cashPayment === 0 ? "bank" : "split";

            const todayDate = getTodayISTDate();

            return {
              id: newTransactionId,
              date: todayDate,
              type: "income",
              billNumber: `CUST-${transactionData.transactionDetails?.billNo || '0000'}`,
              customerSupplier: customerName || "Unknown Customer",
              description: transactionData.items
                ?.map((item) => item.itemName || item.name || "Item")
                .join(", ") || "Customer Sale",
              amount: transactionData.summary?.grandTotal || 0,
              totalReceived: transactionData.paymentDetails?.totalPaymentDone || 0,
              bank: bankPayment || 0,
              cash: cashPayment || 0,
              totalPending: parseFloat(transactionData.summary?.pendingAmount || 0).toFixed(0),
              paymentMode: paymentMode,
              remark: "Auto-generated from customer transaction",
              transactionType: "customer",
              isAutoGenerated: true,
              referenceId: newTransactionId,
              created_at: currentTime
            };
          };

          const roznamaIncomeTransaction = calculateRoznamaTransactionFromCustomer(transactionData, customerName);
          const todayDate = getTodayISTDate();

          const roznamaIndex = updatedRoznama.findIndex(r => {
            try {
              const roznamaDate = convertToYYYYMMDD(r.date);
              return roznamaDate === todayDate;
            } catch (error) {
              return false;
            }
          });

          let openingBalanceCash = 0;
          let openingBalanceBank = 0;

          if (roznamaIndex >= 0) {
            openingBalanceCash = updatedRoznama[roznamaIndex].openingBalanceCash || 0;
            openingBalanceBank = updatedRoznama[roznamaIndex].openingBalanceBank || 0;
          } else {
            const lastRoznama = updatedRoznama.length > 0
              ? updatedRoznama[updatedRoznama.length - 1]
              : null;
            openingBalanceCash = lastRoznama?.closingBalanceCash || 0;
            openingBalanceBank = lastRoznama?.closingBalanceBank || 0;
          }

          if (roznamaIndex >= 0) {
            const existingRoznama = { ...updatedRoznama[roznamaIndex] };
            const existingIncomeTransactions = Array.isArray(existingRoznama.incomeTransactions)
              ? existingRoznama.incomeTransactions
              : [];

            existingRoznama.incomeTransactions = [...existingIncomeTransactions, roznamaIncomeTransaction];

            const transactionAmount = roznamaIncomeTransaction.totalReceived || 0;
            const cashReceived = roznamaIncomeTransaction.cash || 0;
            const bankReceived = roznamaIncomeTransaction.bank || 0;

            existingRoznama.totalIncome = (parseFloat(existingRoznama.totalIncome) || 0) + transactionAmount;
            existingRoznama.totalExpense = parseFloat(existingRoznama.totalExpense) || 0;
            existingRoznama.netProfit = existingRoznama.totalIncome - existingRoznama.totalExpense;

            existingRoznama.closingBalanceCash = (parseFloat(existingRoznama.closingBalanceCash) || 0) + cashReceived;
            existingRoznama.closingBalanceBank = (parseFloat(existingRoznama.closingBalanceBank) || 0) + bankReceived;
            existingRoznama.updated_at = currentTime;
            updatedRoznama[roznamaIndex] = existingRoznama;
          } else {
            const newRoznamaId = `temp-${Date.now()}`;
            const cashReceived = roznamaIncomeTransaction.cash || 0;
            const bankReceived = roznamaIncomeTransaction.bank || 0;
            const transactionAmount = roznamaIncomeTransaction.totalReceived || 0;
            const totalIncome = transactionAmount;
            const totalExpense = 0;
            const netProfit = totalIncome - totalExpense;

            const newRoznama = {
              id: newRoznamaId,
              date: todayDate,
              openingBalanceCash: openingBalanceCash,
              openingBalanceBank: openingBalanceBank,
              closingBalanceCash: openingBalanceCash + cashReceived,
              closingBalanceBank: openingBalanceBank + bankReceived,
              incomeTransactions: [roznamaIncomeTransaction],
              expenseTransactions: [],
              netProfit: netProfit,
              totalIncome: totalIncome,
              totalExpense: totalExpense,
              created_at: currentTime,
              updated_at: currentTime
            };

            updatedRoznama.push(newRoznama);
          }

          updatedRoznama.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB - dateA;
          });

          let updatedGrossStockTransactions = [...(prev.GrossStockTransactions || [])];
          if (grossStockDeductions?.results && grossStockDeductions.results.length > 0) {
            const newTransactions = grossStockDeductions.results
              .filter(d => d.success)
              .map(d => ({
                id: d.transactionId || `gs-tx-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                itemId: d.itemId,
                operation: 'subtract',
                numberOfPieces: d.piecesDeducted || 0,
                weightChanged: d.weightDeducted || 0,
                partyName: transactionData.customerName || 'Customer',
                description: `Sold to ${transactionData.customerName || 'Customer'} - Bill #${transactionData.transactionDetails?.billNo || 'N/A'}`,
                created_at: currentTime,
                updated_at: currentTime
              }));

            updatedGrossStockTransactions = [...newTransactions, ...updatedGrossStockTransactions];
          }

          return {
            ...prev,
            Customers: updatedCustomers,
            CustomerTransactions: updatedTransactions,
            Stock: updatedStock,
            GrossStock: updatedGrossStock,
            GrossStockTransactions: updatedGrossStockTransactions,
            Roznama: updatedRoznama
          };
        });

        clearLocalCache();

        return {
          success: true,
          data: {
            transaction: newTransaction,
            customerUpdate: customerUpdateInfo,
            grossStockDeductions: grossStockDeductions,
            silverUpdates: {
              performed: transactionData.items?.some(item => item.manualItemDetails?.metal === 'Silver') || false,
              count: transactionData.items?.filter(item => item.manualItemDetails?.metal === 'Silver').length || 0
            },
            roznamaUpdate: {
              updated: true,
              transactionType: "income"
            }
          },
          message: response?.data?.message || "Transaction added successfully"
        };
      } else {
        throw new Error(response?.error || "Failed to add transaction and update stock");
      }
    } catch (error) {
      console.error("Error adding transaction and updating stock:", error);
      return { success: false, error: error.message, message: "Failed to add transaction and update stock" };
    } finally {
      setLoading(false);
    }
  };

  // ================================================================
  // COMBINED: removeCustomerTransactionAndUpdateStock
  // ================================================================
  const removeCustomerTransactionAndUpdateStockInContext = async (data) => {
    setLoading(true);
    try {
      const {
        transactionId,
        customerId,
        billNo,
        items = [],
        silverItems = [],
        refund = {},
        cancelledAt
      } = data;

      const { cash = 0, bank = 0, total = 0 } = refund;
      const currentTime = new Date().toISOString();
      const todayDate = getTodayISTDate();

      const transactionIndex = data.CustomerTransactions.findIndex(t => t.id === transactionId);

      if (transactionIndex === -1) {
        throw new Error(`Transaction with ID ${transactionId} not found`);
      }

      const transaction = { ...data.CustomerTransactions[transactionIndex] };
      const originalTransactionDetails = JSON.parse(JSON.stringify(transaction.transactionDetails));

      if (transaction.transactionDetails?.type === 'cancelled') {
        throw new Error(`Transaction ${transactionId} is already cancelled`);
      }

      const updatedTransaction = {
        ...transaction,
        transactionDetails: {
          ...transaction.transactionDetails,
          type: 'cancelled'
        },
        updated_at: currentTime
      };

      const silverUpdateResults = [];
      let updatedSilverStock = { ...data.SilverStock };
      let updatedSilverTransactions = [...(data.SilverTransactions || [])];

      if (silverItems && silverItems.length > 0) {
        silverItems.forEach((silverGroup) => {
          const silverType = silverGroup.silverType;
          const totalWeight = parseFloat(silverGroup.totalWeight) || 0;

          if (totalWeight > 0) {
            const currentWeight = parseFloat(updatedSilverStock[silverType]) || 0;
            const newWeight = currentWeight + totalWeight;

            updatedSilverStock[silverType] = newWeight;
            updatedSilverStock.timestamp = currentTime;

            const silverTransaction = {
              id: `silver-cancel-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              date: todayDate,
              silverType: silverType,
              netWeight: totalWeight,
              operation: "add",
              description: `Returned from customer: Bill ${billNo}`,
              timestamp: currentTime,
              billNo: billNo,
              customerId: customerId,
              transactionId: transactionId,
              reason: "Transaction cancellation - silver returned",
              isLocal: true,
              synced: false
            };

            updatedSilverTransactions = [silverTransaction, ...updatedSilverTransactions];

            silverUpdateResults.push({
              silverType: silverType,
              success: true,
              operation: "add",
              weightChanged: totalWeight,
              previousWeight: currentWeight,
              newWeight: newWeight
            });
          }
        });
      }

      const filteredSilverTransactions = updatedSilverTransactions.filter(silverTrans => {
        const isRelated = (
          silverTrans.billNo === billNo ||
          (silverTrans.description && silverTrans.description.includes(`Bill ${billNo}`)) ||
          (silverTrans.customerId && silverTrans.customerId === customerId) ||
          (silverTrans.transactionId && silverTrans.transactionId === transactionId)
        );
        return !isRelated;
      });

      let customerUpdated = false;
      let currentCustomerBalance = 0;
      let balanceChange = 0;
      let updatedCustomers = [...data.Customers];

      if (customerId) {
        const customerIndex = updatedCustomers.findIndex(c => c.id === customerId);

        if (customerIndex !== -1) {
          const customer = updatedCustomers[customerIndex];
          const oldBalance = parseFloat(customer.currentBalance) || 0;

          const pendingAmount = parseFloat(transaction.summary?.pendingAmount) || 0;
          const paymentDone = parseFloat(transaction.paymentDetails?.totalPaymentDone) || 0;

          if (pendingAmount < 0) {
            balanceChange = pendingAmount + paymentDone;
          } else if (pendingAmount > 0) {
            balanceChange = pendingAmount + paymentDone;
          }

          balanceChange -= total;

          currentCustomerBalance = parseFloat((oldBalance - balanceChange).toFixed(0));

          updatedCustomers[customerIndex] = {
            ...customer,
            currentBalance: currentCustomerBalance,
            updated_at: currentTime
          };

          customerUpdated = true;
        }
      }

      const stockUpdateResults = [];
      let updatedStock = [...data.Stock];

      if (items && items.length > 0) {
        items.forEach((item) => {
          const stockId = item.id;
          const huid = item.huid;
          const name = item.name;
          const originalSerialNo = item.originalSerialNo;
          const newSerialNo = item.newSerialNo;
          const updateData = item.updateData || {};
          const hasConflict = item.hasConflict || false;

          if (!stockId) {
            stockUpdateResults.push({ stockId: 'N/A', huid, name, status: 'skipped', message: 'No stock ID provided' });
            return;
          }

          const stockIndex = updatedStock.findIndex(s => s.id === stockId);

          if (stockIndex === -1) {
            stockUpdateResults.push({ stockId, huid, name, status: 'skipped', message: 'Stock item not found' });
            return;
          }

          let finalSerialNo = originalSerialNo;
          if (hasConflict && newSerialNo) {
            finalSerialNo = newSerialNo;
          }

          try {
            const stockUpdates = {
              ...updateData,
              Sold: updateData.sold || "No",
              SoldDescription: updateData.soldDescription || null,
              updated_at: currentTime
            };

            if (finalSerialNo && finalSerialNo !== originalSerialNo) {
              stockUpdates.SerialNo = finalSerialNo;
            }

            updatedStock[stockIndex] = {
              ...updatedStock[stockIndex],
              ...stockUpdates
            };

            stockUpdateResults.push({
              stockId, huid, name,
              originalSerialNo, newSerialNo: finalSerialNo,
              status: 'updated',
              message: hasConflict ? 'Updated with new serial number due to conflict' : 'Successfully updated'
            });
          } catch (error) {
            stockUpdateResults.push({
              stockId, huid, name, status: 'error',
              message: `Update failed: ${error.message}`
            });
          }
        });
      }

      let updatedRoznama = [...data.Roznama];
      const customerName = customerUpdated
        ? updatedCustomers.find(c => c.id === customerId)?.name || "Unknown Customer"
        : "Unknown Customer";

      const expenseTransaction = {
        id: `expense-${transactionId}-${Date.now()}`,
        type: "expense",
        date: todayDate,
        timestamp: currentTime,
        description: `Transaction Cancelled - Refund: Bill ${billNo} - ${customerName}`,
        customerId: customerId,
        transactionId: transactionId,
        billNo: billNo,
        cash: parseFloat(cash) || 0,
        bank: parseFloat(bank) || 0,
        totalPaid: parseFloat(total) || 0,
        category: "Transaction Refund",
        notes: `Cancelled transaction ${transactionId}, refund issued`,
        isAutoGenerated: true,
        referenceId: transactionId
      };

      const roznamaIndex = updatedRoznama.findIndex(r => {
        try {
          const rDate = new Date(r.date).toISOString().split('T')[0];
          return rDate === todayDate;
        } catch (error) {
          return false;
        }
      });

      if (roznamaIndex >= 0) {
        const existingRoznama = { ...updatedRoznama[roznamaIndex] };
        const existingExpenseTransactions = Array.isArray(existingRoznama.expenseTransactions)
          ? existingRoznama.expenseTransactions
          : [];

        existingRoznama.expenseTransactions = [...existingExpenseTransactions, expenseTransaction];

        existingRoznama.totalIncome = calculateTotal(existingRoznama.incomeTransactions || [], "income");
        existingRoznama.totalExpense = calculateTotal(existingRoznama.expenseTransactions || [], "expense");
        existingRoznama.netProfit = existingRoznama.totalIncome - existingRoznama.totalExpense;

        existingRoznama.closingBalanceCash = parseFloat(existingRoznama.closingBalanceCash || 0) - parseFloat(cash);
        existingRoznama.closingBalanceBank = parseFloat(existingRoznama.closingBalanceBank || 0) - parseFloat(bank);
        existingRoznama.updated_at = currentTime;

        updatedRoznama[roznamaIndex] = existingRoznama;
      } else {
        const newRoznamaId = `temp-${Date.now()}`;
        const lastRoznama = updatedRoznama.length > 0 ? updatedRoznama[updatedRoznama.length - 1] : null;
        const prevClosingCash = lastRoznama?.closingBalanceCash || 0;
        const prevClosingBank = lastRoznama?.closingBalanceBank || 0;
        const expenseCash = parseFloat(cash) || 0;
        const expenseBank = parseFloat(bank) || 0;
        const totalExpenseAmount = expenseCash + expenseBank;

        const newRoznama = {
          id: newRoznamaId,
          date: todayDate,
          openingBalanceCash: prevClosingCash,
          openingBalanceBank: prevClosingBank,
          closingBalanceCash: prevClosingCash - expenseCash,
          closingBalanceBank: prevClosingBank - expenseBank,
          incomeTransactions: [],
          expenseTransactions: [expenseTransaction],
          netProfit: -totalExpenseAmount,
          totalIncome: 0,
          totalExpense: totalExpenseAmount,
          created_at: currentTime,
          updated_at: currentTime
        };

        updatedRoznama.push(newRoznama);
      }

      updatedRoznama.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB - dateA;
      });

      setData(prev => {
        const updatedTransactions = [...prev.CustomerTransactions];
        updatedTransactions[transactionIndex] = updatedTransaction;

        return {
          ...prev,
          Customers: updatedCustomers,
          CustomerTransactions: updatedTransactions,
          Stock: updatedStock,
          SilverStock: updatedSilverStock,
          SilverTransactions: filteredSilverTransactions,
          Roznama: updatedRoznama
        };
      });

      clearLocalCache();

      return {
        success: true,
        data: {
          message: "Transaction cancelled and reversed successfully",
          transactionId: transactionId,
          cancellationDetails: {
            originalType: originalTransactionDetails.type,
            newType: 'cancelled',
            billNo: billNo,
            cancelledAt: cancelledAt || currentTime
          },
          customerUpdate: {
            customerId: customerId,
            balanceUpdated: customerUpdated,
            newBalance: currentCustomerBalance,
            balanceChange: balanceChange
          },
          silverUpdates: {
            performed: silverItems.length > 0,
            count: silverItems.length,
            results: silverUpdateResults
          },
          stockUpdates: {
            performed: items.length > 0,
            count: items.length,
            results: stockUpdateResults,
            itemsWithConflicts: items.filter(item => item.hasConflict).length
          },
          refundDetails: { cash, bank, total },
          roznamaUpdate: { updated: true, transactionType: "expense" },
          timestamps: { cancelled_at: currentTime }
        }
      };
    } catch (error) {
      console.error("Error in removeCustomerTransactionAndUpdateStockInContext:", error);
      return { success: false, error: error.message, message: "Failed to cancel transaction" };
    } finally {
      setLoading(false);
    }
  };

  const removeCustomerTransactionAndUpdateStock = async (data) => {
    setLoading(true);
    try {
      const response = await removeCustomerTransactionAndUpdateStockApi(data);

      if (response?.success) {
        const contextResult = await removeCustomerTransactionAndUpdateStockInContext(data);

        if (contextResult.success) {
          clearLocalCache();
          return {
            success: true,
            data: contextResult.data,
            message: "Transaction cancelled successfully"
          };
        } else {
          throw new Error(contextResult.error || "Failed to update frontend context");
        }
      } else {
        throw new Error(response?.error || "Failed to cancel transaction on server");
      }
    } catch (error) {
      console.error("Error cancelling transaction:", error);
      return { success: false, error: error.message, message: "Failed to cancel transaction" };
    } finally {
      setLoading(false);
    }
  };

  // ================================================================
  // STOCK CRUD
  // ================================================================
  const addStockToContext = (stockData) => {
    let soldDescription = stockData.SoldDescription;
    if (soldDescription && typeof soldDescription === 'object') {
      soldDescription = JSON.stringify(soldDescription);
    }
    const stockWithDefaults = {
      ...stockData,
      Sold: stockData.Sold || "No",
      SoldDescription: soldDescription || null
    };
    return createItem('Stock', stockWithDefaults, addStock);
  };

  const updateStockInContext = (id, stockData) => {
    if (stockData.Sold === "Yes" && !stockData.SoldDescription) {
      const stockWithDescription = {
        ...stockData,
        SoldDescription: stockData.SoldDescription || JSON.stringify({
          sold_date: new Date().toISOString().split('T')[0],
          sold_bill_no: "MANUAL",
          notes: "Manually marked as sold"
        })
      };
      return updateItem('Stock', id, stockWithDescription, updateStock);
    }
    if (stockData.SoldDescription && typeof stockData.SoldDescription === 'object') {
      const stockWithStringifiedDesc = {
        ...stockData,
        SoldDescription: JSON.stringify(stockData.SoldDescription)
      };
      return updateItem('Stock', id, stockWithStringifiedDesc, updateStock);
    }
    return updateItem('Stock', id, stockData, updateStock);
  };

  const deleteStockFromContext = (id) => deleteItem('Stock', id, deleteStock);

  const addBulkStockToContext = async (data) => {
    setLoading(true);
    try {
      const response = await addBulkStock(data);

      if (response?.success) {
        const createdItems = response.data?.itemsCreated || [];
        const serialNumbers = response.data?.serialNumbers || [];
        const totalItems = response.data?.totalItems || 0;

        const newStockItems = createdItems.map((item, index) => {
          const weightData = data.itemWeights?.[index] || {};

          return {
            id: `temp-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 6)}`,
            supplierName: data.supplierName || '',
            supplierShortcut: data.supplierShortcut || '',
            name: data.name || '',
            metal: data.metal || '',
            silverType: data.silverType || '',
            melting: data.melting || '',
            grossWeight: item.grossWeight || weightData.grossWeight || '0',
            lessWeight: item.lessWeight || weightData.lessWeight || '0',
            weight: item.weight || weightData.netWeight || '0',
            silverRate: data.silverRate || '',
            labour: data.labour || '',
            goldCuttingRate: data.goldCuttingRate || '',
            wastage: data.wastage || '',
            makingCharges: data.makingCharges || '',
            serialNo: item.serialNo?.toString() || weightData.serialNo || '',
            description: data.description || '',
            itemDetail: data.itemDetail || '',
            miscitemName: data.miscitemName || '',
            miscitemShortcut: data.miscitemShortcut || '',
            huid: data.huid || '',
            qr: item.qr || data.qr || `JWL${Date.now()}-${index}`,
            sold: data.sold || "No",
            price: item.price || '0',
            created_at: response.data?.timestamps?.created_at || new Date().toISOString(),
            updated_at: response.data?.timestamps?.updated_at || new Date().toISOString()
          };
        });

        setData(prev => ({
          ...prev,
          Stock: [...prev.Stock, ...newStockItems]
        }));

        clearLocalCache();

        return {
          success: true,
          data: {
            itemsCreated: newStockItems,
            serialNumbers: serialNumbers,
            totalItems: totalItems,
            rawResponse: response.data
          },
          message: response.data?.message || `${totalItems} item(s) added successfully`
        };
      } else {
        throw new Error(response?.error || response?.message || "Failed to add bulk stock");
      }
    } catch (error) {
      console.error("Error adding bulk stock:", error);
      return { success: false, error: error.message, message: error.message || "Failed to add bulk stock items" };
    } finally {
      setLoading(false);
    }
  };

  // ================================================================
  // SUPPLIER CRUD
  // ================================================================
  const addSupplierToContext = (supplierData) => createItem('Suppliers', supplierData, addSupplier);
  const updateSupplierInContext = (id, supplierData) => updateItem('Suppliers', id, supplierData, updateSupplier);
  const deleteSupplierFromContext = (id) => deleteItem('Suppliers', id, deleteSupplier);

  // ================================================================
  // SUPPLIER TRANSACTIONS
  // ================================================================
  const addSupplierTransactionToContext = async (transactionData) => {
    setLoading(true);
    try {
      const response = await addSupplierTransaction(transactionData);

      if (response?.success) {
        const newTransaction = response.data;
        const supplierId = transactionData.supplier;
        const summary = transactionData.summary || {};

        const grandTotal = parseFloat(summary.grandTotal) || 0;
        const fineBalance = parseFloat(summary.fineBalance) || 0;
        const finePayment = parseFloat(summary.finePayment) || 0;

        const payments = transactionData.payments || {};
        const cashPayment = parseFloat(payments.cash) || 0;
        const bankPayment = parseFloat(payments.bank) || 0;

        const balanceType = transactionData.balanceType || '';

        setData(prev => {
          const currentTime = new Date().toISOString();

          const updatedTransactions = [...prev.SupplierTransactions, newTransaction];

          const updatedSuppliers = prev.Suppliers.map(supplier => {
            if (supplier.id === supplierId) {
              let newBalanceCash = parseFloat(supplier.balanceValueCash) || 0;
              let newBalanceBank = parseFloat(supplier.balanceValueBank) || 0;
              let newBalanceFine = parseFloat(supplier.balanceFine) || 0;

              if (cashPayment > 0) newBalanceCash -= cashPayment;
              if (bankPayment > 0) newBalanceBank -= bankPayment;
              if (finePayment > 0) newBalanceFine -= finePayment;

              if (grandTotal > 0) {
                if (balanceType === 'cash') newBalanceCash += grandTotal;
                else if (balanceType === 'bank') newBalanceBank += grandTotal;
              }

              if (fineBalance > 0) newBalanceFine += fineBalance;

              return {
                ...supplier,
                balanceValueCash: newBalanceCash,
                balanceValueBank: newBalanceBank,
                balanceFine: newBalanceFine,
                updated_at: currentTime
              };
            }
            return supplier;
          });

          let updatedRoznama = [...prev.Roznama];
          const todayDate = getTodayISTDate();
          const transactionId = newTransaction.id;

          const roznamaExpenseTransaction = {
            id: transactionId,
            date: todayDate,
            type: "expense",
            billNumber: `SUPP-${transactionId.substring(0, 8)}`,
            customerSupplier: prev.Suppliers.find(s => s.id === supplierId)?.name || "Unknown Supplier",
            description: transactionData.items
              ?.map((item) => item.itemName || item.name || "Item")
              .join(", ") || "Supplier Purchase",
            amount: grandTotal || 0,
            totalPaid: summary.paymentDone || 0,
            fine: fineBalance || 0,
            bank: bankPayment || 0,
            cash: cashPayment || 0,
            finePaid: finePayment || 0,
            totalPending: parseFloat(summary.pendingAmount || 0).toFixed(0),
            paymentMode: bankPayment > 0 && cashPayment === 0 ? "bank" :
              bankPayment === 0 && cashPayment > 0 ? "cash" : "split",
            remark: "Auto-generated from supplier transaction",
            transactionType: "supplier",
            isAutoGenerated: true,
            referenceId: transactionId,
            created_at: currentTime
          };

          const roznamaIndex = updatedRoznama.findIndex(r => {
            try {
              const rDate = new Date(r.date).toISOString().split('T')[0];
              return rDate === todayDate;
            } catch (error) {
              return false;
            }
          });

          if (roznamaIndex >= 0) {
            const existingRoznama = { ...updatedRoznama[roznamaIndex] };
            const existingExpenseTransactions = Array.isArray(existingRoznama.expenseTransactions)
              ? existingRoznama.expenseTransactions
              : [];

            existingRoznama.expenseTransactions = [...existingExpenseTransactions, roznamaExpenseTransaction];

            const transactionAmount = parseFloat(roznamaExpenseTransaction.totalPaid) || 0;

            existingRoznama.totalExpense = (parseFloat(existingRoznama.totalExpense) || 0) + transactionAmount;
            existingRoznama.netProfit = (parseFloat(existingRoznama.totalIncome) || 0) - (parseFloat(existingRoznama.totalExpense) || 0);

            const cashPaid = parseFloat(roznamaExpenseTransaction.cash) || 0;
            const bankPaid = parseFloat(roznamaExpenseTransaction.bank) || 0;

            existingRoznama.closingBalanceCash = (parseFloat(existingRoznama.closingBalanceCash) || 0) - cashPaid;
            existingRoznama.closingBalanceBank = (parseFloat(existingRoznama.closingBalanceBank) || 0) - bankPaid;
            existingRoznama.updated_at = currentTime;

            updatedRoznama[roznamaIndex] = existingRoznama;
          } else {
            let prevRoznama = updatedRoznama[updatedRoznama.length - 1];
            const newRoznama = {
              id: `temp-${Date.now()}`,
              date: todayDate,
              openingBalanceCash: prevRoznama?.closingBalanceCash,
              openingBalanceBank: prevRoznama?.closingBalanceBank,
              closingBalanceCash: Number(prevRoznama?.closingBalanceCash) - cashPayment,
              closingBalanceBank: Number(prevRoznama?.closingBalanceBank) - bankPayment,
              incomeTransactions: [],
              expenseTransactions: [roznamaExpenseTransaction],
              netProfit: 0 - cashPayment + bankPayment,
              totalIncome: 0,
              totalExpense: cashPayment + bankPayment,
              created_at: currentTime,
              updated_at: currentTime
            };
            updatedRoznama.push(newRoznama);
          }

          return {
            ...prev,
            Suppliers: updatedSuppliers,
            SupplierTransactions: updatedTransactions,
            Roznama: updatedRoznama
          };
        });

        clearLocalCache();

        return { success: true, data: newTransaction, message: "Supplier transaction added and balances updated successfully" };
      } else {
        throw new Error(response?.error || "Failed to add supplier transaction");
      }
    } catch (error) {
      console.error("Error adding supplier transaction:", error);
      return { success: false, error: error.message, message: "Failed to add supplier transaction" };
    } finally {
      setLoading(false);
    }
  };

  const updateSupplierTransactionInContext = (id, transactionData) => updateItem('SupplierTransactions', id, transactionData, updateSupplierTransaction);
  const deleteSupplierTransactionFromContext = (id) => deleteItem('SupplierTransactions', id, deleteSupplierTransaction);

  // ================================================================
  // GIRVI
  // ================================================================
  const addGirviToContext = (girviData) => createItem('Girvi', girviData, addGirvi);
  const updateGirviInContext = (id, girviData) => updateItem('Girvi', id, girviData, updateGirvi);
  const deleteGirviFromContext = (id) => deleteItem('Girvi', id, deleteGirvi);

  const addGirviPaymentToContext = (paymentData) => createItem('GirviPayments', paymentData, addGirviPayment);
  const updateGirviPaymentInContext = (id, paymentData) => updateItem('GirviPayments', id, paymentData, updateGirviPayment);
  const deleteGirviPaymentFromContext = (id) => deleteItem('GirviPayments', id, deleteGirviPayment);

  // ================================================================
  // PARTIAL PAYMENT
  // ================================================================
  const recordPartialPayment = async (transactionId, paymentData) => {
    setLoading(true);
    try {
      const transactionIndex = data.CustomerTransactions.findIndex(t => t.id === transactionId);

      if (transactionIndex === -1) {
        throw new Error(`Transaction with ID ${transactionId} not found`);
      }

      const transaction = { ...data.CustomerTransactions[transactionIndex] };

      const newPaymentEntry = {
        id: generatePaymentId(),
        date: paymentData.date || new Date().toISOString(),
        amount: paymentData.amount,
        paymentModes: {
          cash: paymentData.cash || 0,
          online: paymentData.online || 0,
          cheque: paymentData.cheque || 0,
          other: paymentData.other || 0,
          advanceBreakup: paymentData.advanceBreakup || {}
        },
        note: paymentData.note || `Payment on ${new Date(paymentData.date || new Date()).toLocaleDateString()}`,
        recordedBy: paymentData.recordedBy || "System"
      };

      const existingPayments = transaction.paymentDetails?.paymentHistory || [];
      const updatedPayments = [...existingPayments, newPaymentEntry];

      const totalPaid = updatedPayments.reduce((sum, p) => sum + p.amount, 0);
      const originalAmount = transaction.transactionDetails?.originalBillAmount || transaction.summary?.grandTotal || 0;
      const newBalance = originalAmount - totalPaid;
      const isFullyPaid = newBalance <= 0;

      const updatedTransaction = {
        ...transaction,
        paymentDetails: {
          ...transaction.paymentDetails,
          paymentHistory: updatedPayments,
          totalPaymentDone: totalPaid,
          cash: (transaction.paymentDetails?.cash || 0) + (paymentData.cash || 0),
          online: (transaction.paymentDetails?.online || 0) + (paymentData.online || 0),
          cheque: (transaction.paymentDetails?.cheque || 0) + (paymentData.cheque || 0),
          other: (transaction.paymentDetails?.other || 0) + (paymentData.other || 0),
        },
        summary: {
          ...transaction.summary,
          paymentDone: totalPaid,
          pendingAmount: newBalance > 0 ? newBalance : 0
        },
        transactionDetails: {
          ...transaction.transactionDetails,
          balanceDue: newBalance > 0 ? newBalance : 0,
          isFullyPaid: isFullyPaid,
          paymentStatus: isFullyPaid ? "paid" : "partial",
          lastPaymentDate: newPaymentEntry.date,
          lastPaymentAmount: newPaymentEntry.amount
        },
        updated_at: new Date().toISOString()
      };

      let updatedCustomers = [...data.Customers];
      if (transaction.customer) {
        const customerIndex = updatedCustomers.findIndex(c => c.id === transaction.customer);
        if (customerIndex !== -1) {
          const currentBalance = parseFloat(updatedCustomers[customerIndex].currentBalance) || 0;
          updatedCustomers[customerIndex] = {
            ...updatedCustomers[customerIndex],
            currentBalance: currentBalance - paymentData.amount,
            updated_at: new Date().toISOString()
          };
        }
      }

      let updatedRoznama = [...data.Roznama];
      const todayDate = getTodayISTDate();
      const customerName = updatedCustomers.find(c => c.id === transaction.customer)?.name || "Unknown Customer";

      const roznamaPaymentEntry = {
        id: `payment-${newPaymentEntry.id}`,
        date: todayDate,
        type: "income",
        billNumber: `BILL-${transaction.transactionDetails?.billNo || '0000'}`,
        customerSupplier: customerName,
        description: `Partial payment received - Bill #${transaction.transactionDetails?.billNo}`,
        amount: paymentData.amount,
        totalReceived: paymentData.amount,
        bank: (paymentData.online || 0) + (paymentData.cheque || 0),
        cash: paymentData.cash || 0,
        totalPending: newBalance,
        paymentMode: "partial",
        remark: paymentData.note || "Partial payment",
        transactionType: "customer",
        isAutoGenerated: true,
        referenceId: transactionId,
        paymentId: newPaymentEntry.id,
        created_at: new Date().toISOString()
      };

      const roznamaIndex = updatedRoznama.findIndex(r => {
        try {
          const rDate = new Date(r.date).toISOString().split('T')[0];
          return rDate === todayDate;
        } catch (error) {
          return false;
        }
      });

      if (roznamaIndex >= 0) {
        const existingRoznama = { ...updatedRoznama[roznamaIndex] };
        const existingIncomeTransactions = Array.isArray(existingRoznama.incomeTransactions)
          ? existingRoznama.incomeTransactions
          : [];

        existingRoznama.incomeTransactions = [...existingIncomeTransactions, roznamaPaymentEntry];
        existingRoznama.totalIncome = (parseFloat(existingRoznama.totalIncome) || 0) + paymentData.amount;
        existingRoznama.netProfit = existingRoznama.totalIncome - (parseFloat(existingRoznama.totalExpense) || 0);
        existingRoznama.closingBalanceCash = (parseFloat(existingRoznama.closingBalanceCash) || 0) + (paymentData.cash || 0);
        existingRoznama.closingBalanceBank = (parseFloat(existingRoznama.closingBalanceBank) || 0) + (paymentData.online || 0) + (paymentData.cheque || 0);
        existingRoznama.updated_at = new Date().toISOString();

        updatedRoznama[roznamaIndex] = existingRoznama;
      } else {
        const lastRoznama = updatedRoznama.length > 0 ? updatedRoznama[updatedRoznama.length - 1] : null;
        const newRoznama = {
          id: `temp-${Date.now()}`,
          date: todayDate,
          openingBalanceCash: lastRoznama?.closingBalanceCash || 0,
          openingBalanceBank: lastRoznama?.closingBalanceBank || 0,
          closingBalanceCash: (lastRoznama?.closingBalanceCash || 0) + (paymentData.cash || 0),
          closingBalanceBank: (lastRoznama?.closingBalanceBank || 0) + (paymentData.online || 0) + (paymentData.cheque || 0),
          incomeTransactions: [roznamaPaymentEntry],
          expenseTransactions: [],
          netProfit: paymentData.amount,
          totalIncome: paymentData.amount,
          totalExpense: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        updatedRoznama.push(newRoznama);
      }

      setData(prev => {
        const updatedTransactions = [...prev.CustomerTransactions];
        updatedTransactions[transactionIndex] = updatedTransaction;

        return {
          ...prev,
          Customers: updatedCustomers,
          CustomerTransactions: updatedTransactions,
          Roznama: updatedRoznama
        };
      });

      const response = await updateCustomerTransaction(transactionId, updatedTransaction);

      if (response?.success) {
        clearLocalCache();
        return {
          success: true,
          data: {
            transaction: updatedTransaction,
            payment: newPaymentEntry,
            newBalance: newBalance,
            isFullyPaid: isFullyPaid
          },
          message: isFullyPaid ? "Payment completed! Bill is now fully paid." : "Partial payment recorded successfully"
        };
      } else {
        throw new Error(response?.error || "Failed to update backend");
      }
    } catch (error) {
      console.error("Error recording partial payment:", error);
      return { success: false, error: error.message, message: "Failed to record payment" };
    } finally {
      setLoading(false);
    }
  };

  const getPaymentHistory = (transactionId) => {
    const transaction = data.CustomerTransactions.find(t => t.id === transactionId);
    if (!transaction) return [];
    return transaction.paymentDetails?.paymentHistory || [];
  };

  const getPendingPayments = (filters = {}) => {
    let pendingTransactions = data.CustomerTransactions.filter(t => {
      const isPending = t.transactionDetails?.balanceDue > 0 && !t.transactionDetails?.isFullyPaid && t.transactionDetails?.type;
      if (!isPending) return false;

      if (filters.customerId && t.customer !== filters.customerId) return false;
      if (filters.startDate) {
        const txDate = new Date(t.date);
        const startDate = new Date(filters.startDate);
        if (txDate < startDate) return false;
      }
      if (filters.endDate) {
        const txDate = new Date(t.date);
        const endDate = new Date(filters.endDate);
        if (txDate > endDate) return false;
      }
      return true;
    });

    pendingTransactions.sort((a, b) => new Date(a.date) - new Date(b.date));

    return pendingTransactions.map(t => ({
      id: t.id,
      billNo: t.transactionDetails?.billNo,
      customerId: t.customer,
      customerName: data.Customers.find(c => c.id === t.customer)?.name || "Unknown",
      date: t.date,
      originalAmount: t.transactionDetails?.originalBillAmount || t.summary?.grandTotal,
      paidAmount: t.paymentDetails?.totalPaymentDone || 0,
      balanceDue: t.transactionDetails?.balanceDue,
      lastPaymentDate: t.paymentDetails?.paymentHistory?.[t.paymentDetails.paymentHistory.length - 1]?.date,
      paymentHistory: t.paymentDetails?.paymentHistory || []
    }));
  };

  const getCustomerBalanceSummary = (customerId) => {
    const customerTransactions = data.CustomerTransactions.filter(t => t.customer === customerId);

    const totalBilled = customerTransactions.reduce((sum, t) =>
      sum + (t.transactionDetails?.originalBillAmount || t.summary?.grandTotal || 0), 0);

    const totalPaid = customerTransactions.reduce((sum, t) =>
      sum + (t.paymentDetails?.totalPaymentDone || 0), 0);

    const totalBalance = customerTransactions.reduce((sum, t) =>
      sum + (t.transactionDetails?.balanceDue || 0), 0);

    const pendingTransactions = customerTransactions.filter(t =>
      t.transactionDetails?.balanceDue > 0 && !t.transactionDetails?.isFullyPaid
    );

    return {
      customerId,
      totalBilled,
      totalPaid,
      totalBalance,
      pendingCount: pendingTransactions.length,
      pendingTransactions: pendingTransactions.map(t => ({
        id: t.id,
        billNo: t.transactionDetails?.billNo,
        date: t.date,
        amount: t.transactionDetails?.originalBillAmount || t.summary?.grandTotal,
        paid: t.paymentDetails?.totalPaymentDone || 0,
        balance: t.transactionDetails?.balanceDue,
        lastPaymentDate: t.paymentDetails?.paymentHistory?.[t.paymentDetails.paymentHistory.length - 1]?.date
      }))
    };
  };

  // ================================================================
  // SEARCH
  // ================================================================
  const searchData = useCallback((searchTerm) => {
    const results = {};
    const searchLower = searchTerm.toLowerCase();

    Object.keys(data).forEach(dataType => {
      results[dataType] = (data[dataType] || []).filter(item => {
        return Object.values(item || {}).some(value =>
          String(value).toLowerCase().includes(searchLower)
        );
      });
    });

    return results;
  }, [data]);

  // ================================================================
  // STATS
  // ================================================================
  const getStats = () => ({
    customers: data.Customers.length,
    transactions: data.CustomerTransactions.length,
    stock: data.Stock.length,
    suppliers: data.Suppliers.length,
    supplierTransactions: data.SupplierTransactions.length,
    girvi: data.Girvi.length,
    girviPayments: data.GirviPayments.length,
    companyDetails: data.CompanyDetails.length,
    silverTransactions: (data.SilverTransactions || []).length,
    eInvoices: (data.EInvoices || []).length,
  });

  // ================================================================
  // CONTEXT VALUE
  // ================================================================
  const value = {
    // Loading state
    loading,
    refreshing,

    // Advance payments
    recordAdvancePayment,
    fetchCustomerAdvanceBalance,
    applyAdvanceToBill,
    getAllAdvancePaymentsList,
    getAdvanceSummary,
    getCustomerAdvanceHistory,
    addAdvancePayment,
    withdrawAdvance,

    // Authentication state
    currentUser,
    isAuthenticated,

    // Payment management
    recordPartialPayment,
    getPaymentHistory,
    getPendingPayments,
    getCustomerBalanceSummary,

    // Data access
    customers: data.Customers,
    customerTransactions: data.CustomerTransactions,
    stock: data.Stock,
    suppliers: data.Suppliers,
    supplierTransactions: data.SupplierTransactions,
    girvi: data.Girvi,
    girviPayments: data.GirviPayments,
    roznama: data.Roznama,
    companyDetails: data.CompanyDetails,
    silverStock: data.SilverStock,
    silverTransactions: data.SilverTransactions,
    schemeCard: data.SchemeCard,
    advancePayments: data.AdvancePayments,
    grossStock: data.GrossStock,
    grossStockTransactions: data.GrossStockTransactions,
    urdPurchase: data.URDPurchase,
    eInvoices: data.EInvoices,
    addEInvoice: addEInvoiceToContext,
    updateEInvoice: updateEInvoiceInContext,
    deleteEInvoice: deleteEInvoiceFromContext,

    // Generic
    updateData,
    getDataByType,
    addDataType,
    searchData,

    // Refresh
    refresh: forceRefresh,

    // Customer
    addCustomer: addCustomerToContext,
    updateCustomer: updateCustomerInContext,
    deleteCustomer: deleteCustomerFromContext,

    // URD
    addUrdPurchase: addUrdPurchaseToContext,
    updateUrdPurchase: updateUrdPurchaseInContext,
    deleteUrdPurchase: deleteUrdPurchaseFromContext,

    // Scheme
    addSchemeCard: addSchemeCardToContext,
    updateSchemeCard: updateSchemeCardInContext,
    deleteSchemeCard: deleteSchemeCardFromContext,

    // Gross Stock
    getGrossStock: getGrossStockFromContext,
    addGrossStockItem: addGrossStockItemToContext,
    addStockTransaction: addStockTransactionToContext,
    getGrossStockTransactions: getGrossStockTransactionsFromContext,
    getStockSummary: getStockSummaryFromContext,
    getCurrentStock: getCurrentStockFromContext,
    updateStockTransaction: updateStockTransactionInContext,
    deleteStockTransaction: deleteStockTransactionFromContext,
    bulkAddStockTransactions: bulkAddStockTransactionsToContext,
    getStockByMetal: getStockByMetalFromContext,
    searchItems: searchItemsFromContext,
    getItemTransactionHistory: getItemTransactionHistoryFromContext,
    getStockValueSummary: getStockValueSummaryFromContext,

    // Transactions
    addCustomerTransaction: addCustomerTransactionToContext,
    updateCustomerTransaction: updateCustomerTransactionInContext,
    deleteCustomerTransaction: deleteCustomerTransactionFromContext,
    addCustomerTransactionAndUpdateStock,
    removeCustomerTransactionAndUpdateStock,

    // Stock
    addStock: addStockToContext,
    updateStock: updateStockInContext,
    deleteStock: deleteStockFromContext,
    addBulkStock: addBulkStockToContext,
    formatSoldDescription,
    markStockAsSold,
    getSoldItems,
    getSoldDescription,

    // Supplier
    addSupplier: addSupplierToContext,
    updateSupplier: updateSupplierInContext,
    deleteSupplier: deleteSupplierFromContext,

    // Supplier Tx
    addSupplierTransaction: addSupplierTransactionToContext,
    updateSupplierTransaction: updateSupplierTransactionInContext,
    deleteSupplierTransaction: deleteSupplierTransactionFromContext,

    // Girvi
    addGirvi: addGirviToContext,
    updateGirvi: updateGirviInContext,
    deleteGirvi: deleteGirviFromContext,
    addGirviPayment: addGirviPaymentToContext,
    updateGirviPayment: updateGirviPaymentInContext,
    deleteGirviPayment: deleteGirviPaymentFromContext,

    // Roznama
    closeRoznamaDay: closeRoznamaDayInContext,
    addRoznama: addRoznamaToContext,
    updateRoznama: updateRoznamaInContext,
    deleteRoznama: deleteRoznamaFromContext,
    findRoznamaByDate,
    saveRoznama: addOrUpdateRoznamaInContext,

    // Company
    getCompanyDetails: getCompanyDetailsFromContext,
    updateCompanyDetails: updateCompanyDetailsInContext,
    addCompanyDetails: addCompanyDetailsToContext,

    // Auth functions
    login: loginInContext,
    logout: logoutInContext,
    sendPasswordResetOtp: sendPasswordResetOtpInContext,
    verifyPasswordResetOtp: verifyPasswordResetOtpInContext,
    resetPassword: resetPasswordInContext,
    updatePassword: updatePasswordInContext,

    // Stats
    getStats
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};